import { useState } from 'react';
import { Switch, Slider, Typography, Space, Divider, InputNumber, Button, Input } from 'antd';
import { EnvironmentOutlined, AimOutlined } from '@ant-design/icons';
import useGisStore from '../../stores/gisStore';

const { Text } = Typography;

const PRESET_LOCATIONS = [
  { name: '北京', lon: 116.397428, lat: 39.90923 },
  { name: '上海', lon: 121.473701, lat: 31.230416 },
  { name: '广州', lon: 113.264385, lat: 23.129112 },
  { name: '深圳', lon: 114.057868, lat: 22.543099 },
  { name: '杭州', lon: 120.153576, lat: 30.287459 },
  { name: '成都', lon: 104.065735, lat: 30.659462 },
  { name: '南宁', lon: 108.320004, lat: 22.82402 },
  { name: '玉林', lon: 110.154393, lat: 22.633319 },
];

export default function GisControlPanel() {
  const { gisState, setCamera, setViewerMode, cursorPosition } = useGisStore();
  const { viewerMode, enableLighting, showTerrain, showSkyAtmosphere, depthTestAgainstTerrain } = gisState;

  const [flyLon, setFlyLon] = useState(116.397428);
  const [flyLat, setFlyLat] = useState(39.90923);
  const [flyHeight, setFlyHeight] = useState(1000);

  const handleFlyTo = () => {
    setCamera({ longitude: flyLon, latitude: flyLat, height: flyHeight });
  };

  const handleFlyToPreset = (preset: typeof PRESET_LOCATIONS[0]) => {
    setFlyLon(preset.lon);
    setFlyLat(preset.lat);
    setCamera({ longitude: preset.lon, latitude: preset.lat, height: flyHeight });
  };

  return (
    <div style={{ padding: 12 }}>
      <Text strong style={{ fontSize: 13 }}>GIS 控制</Text>
      <Divider style={{ margin: '8px 0' }} />

      <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
        {/* Coordinate Display */}
        {cursorPosition && (
          <div style={{ padding: 8, background: '#1a1a2e', borderRadius: 4, border: '1px solid #30475e' }}>
            <Text style={{ fontSize: 11, color: '#888', display: 'block' }}>鼠标坐标</Text>
            <Text style={{ fontSize: 12, color: '#e0e0e0' }}>
              {cursorPosition.lon.toFixed(6)}°, {cursorPosition.lat.toFixed(6)}°
            </Text>
            <Text style={{ fontSize: 10, color: '#666', display: 'block' }}>
              高度: {cursorPosition.height.toFixed(1)}m
            </Text>
          </div>
        )}

        {/* Fly To */}
        <div>
          <Text style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 8 }}>
            <EnvironmentOutlined /> 飞往位置
          </Text>
          <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
            <InputNumber
              size="small"
              value={flyLon}
              onChange={(v) => v !== null && setFlyLon(v)}
              style={{ flex: 1 }}
              step={0.01}
              placeholder="经度"
            />
            <InputNumber
              size="small"
              value={flyLat}
              onChange={(v) => v !== null && setFlyLat(v)}
              style={{ flex: 1 }}
              step={0.01}
              placeholder="纬度"
            />
          </div>
          <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
            <InputNumber
              size="small"
              value={flyHeight}
              onChange={(v) => v !== null && setFlyHeight(v)}
              style={{ flex: 1 }}
              min={100}
              max={50000}
              step={100}
              addonAfter="m"
            />
            <Button size="small" type="primary" icon={<AimOutlined />} onClick={handleFlyTo}>
              飞往
            </Button>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {PRESET_LOCATIONS.map((preset) => (
              <Button
                key={preset.name}
                size="small"
                onClick={() => handleFlyToPreset(preset)}
                style={{ fontSize: 10 }}
              >
                {preset.name}
              </Button>
            ))}
          </div>
        </div>

        <Divider style={{ margin: '4px 0' }} />

        {/* Settings */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Text style={{ fontSize: 12, color: '#888' }}>地形</Text>
          <Switch size="small" checked={showTerrain}
            onChange={(v) => useGisStore.setState({ gisState: { ...gisState, showTerrain: v } })} />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Text style={{ fontSize: 12, color: '#888' }}>大气散射</Text>
          <Switch size="small" checked={showSkyAtmosphere}
            onChange={(v) => useGisStore.setState({ gisState: { ...gisState, showSkyAtmosphere: v } })} />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Text style={{ fontSize: 12, color: '#888' }}>光照</Text>
          <Switch size="small" checked={enableLighting}
            onChange={(v) => useGisStore.setState({ gisState: { ...gisState, enableLighting: v } })} />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Text style={{ fontSize: 12, color: '#888' }}>地形深度测试</Text>
          <Switch size="small" checked={depthTestAgainstTerrain}
            onChange={(v) => useGisStore.setState({ gisState: { ...gisState, depthTestAgainstTerrain: v } })} />
        </div>

        <Divider style={{ margin: '4px 0' }} />

        {/* Viewer Mode：2026-09-01｜球体（3D 大圆球） / 平面（2D 地图）两种视图 */}
        <div>
          <Text style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>视图模式</Text>
          <div style={{ display: 'flex', gap: 4 }}>
            <Button
              size="small"
              type={viewerMode === '3d' ? 'primary' : 'default'}
              onClick={() => setViewerMode('3d')}
              style={{ flex: 1, fontSize: 10 }}
            >
              球体 3D
            </Button>
            <Button
              size="small"
              type={viewerMode === '2d' ? 'primary' : 'default'}
              onClick={() => setViewerMode('2d')}
              style={{ flex: 1, fontSize: 10 }}
            >
              平面 2D
            </Button>
          </div>
        </div>
      </Space>
    </div>
  );
}
